import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  useTheme,
  Chip
} from '@mui/material';
import { InfoCircle, Call, MessageText } from 'iconsax-react';

interface SessionEndDialogProps {
  open: boolean;
  sessionType: 'CALL' | 'CHAT' | string;
  sessionData?: {
    fromName?: string;
    key?: string;
    status?: string;
  };
  onCancel: () => void;
  onEndSession: () => void;
  loading?: boolean;
}

const SessionEndDialog: React.FC<SessionEndDialogProps> = ({
  open,
  sessionType,
  sessionData,
  onCancel,
  onEndSession,
  loading = false
}) => {
  const theme = useTheme();
  
  const isVideoCall = sessionType !== 'CHAT';
  const sessionTypeText = isVideoCall ? 'Video Call' : 'Chat Session';
  const sessionIcon = isVideoCall ? Call : MessageText;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1,
          maxWidth: '400px'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: isVideoCall ? theme.palette.primary.lighter : theme.palette.info.lighter
            }}
          >
            {React.createElement(sessionIcon, { 
              size: 20, 
              color: isVideoCall ? theme.palette.primary.main : theme.palette.info.main 
            })}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {sessionTypeText} in Progress
            </Typography>
            {sessionData?.key && (
              <Typography variant="caption" color="text.secondary">
                Session ID: {sessionData.key}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 0 }}>
        <Box sx={{ mb: 2 }}>
          {sessionData?.fromName && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Connected with:
              </Typography>
              <Chip 
                label={sessionData.fromName}
                size="small"
                sx={{ 
                  backgroundColor: theme.palette.grey[100],
                  fontWeight: 500
                }}
              />
            </Box>
          )}
          
          <Typography variant="body1" color="text.secondary">
            You are currently in an active {sessionTypeText.toLowerCase()}
            {sessionData?.fromName ? ` with ${sessionData.fromName}` : ''}. 
            Refreshing the page will end the session.
          </Typography>
        </Box>
        
        <Box 
          sx={{ 
            p: 2, 
            backgroundColor: theme.palette.warning.lighter,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1
          }}
        >
          <InfoCircle size={16} color={theme.palette.warning.main} style={{ marginTop: 2 }} />
          <Typography variant="caption" color="warning.dark">
            This action cannot be undone.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ pt: 2, px: 3, pb: 2 }}>
        <Button
          onClick={onCancel}
          variant="outlined"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        <Button
          onClick={onEndSession}
          variant="contained"
          color="error"
          disabled={loading}
          sx={{ minWidth: 140 }}
        >
          {loading ? 'Ending Session...' : 'End Session & Refresh'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionEndDialog;
